import React, { useEffect, useMemo, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Button,
  Space,
  Typography,
  Select,
  Input,
  InputNumber,
  Table,
  Tag,
  Statistic,
  Drawer,
  Modal,
  Form,
  Divider,
  message,
} from 'antd';
import { PlusOutlined, DownloadOutlined, UploadOutlined, EnvironmentOutlined, EditOutlined, DeleteOutlined, CompassOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const WhiteLabelLocations = () => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [form] = Form.useForm();

  // Filters
  const [client, setClient] = useState('all');
  const [stateCode, setStateCode] = useState('');
  const [city, setCity] = useState('');
  const [latMin, setLatMin] = useState();
  const [latMax, setLatMax] = useState();
  const [lngMin, setLngMin] = useState();
  const [lngMax, setLngMax] = useState();

  // Mock clients list for filter
  const clients = ['all', 'Acme Co', 'Northwind', 'Contoso', 'AdventureWorks'];

  // Mock data (replace with API results)
  const mockData = useMemo(() => ([
    { id: 1, city: 'San Francisco', state: 'CA', latitude: 37.7749, longitude: -122.4194, zones: 5, insights: 12 },
    { id: 2, city: 'Los Angeles', state: 'CA', latitude: 34.0522, longitude: -118.2437, zones: 8, insights: 18 },
    { id: 3, city: 'Seattle', state: 'WA', latitude: 47.6062, longitude: -122.3321, zones: 3, insights: 7 },
    { id: 4, city: 'Austin', state: 'TX', latitude: 30.2672, longitude: -97.7431, zones: 4, insights: 9 },
    { id: 5, city: 'Miami', state: 'FL', latitude: 25.7617, longitude: -80.1918, zones: 2, insights: 5 },
  ]), []);

  const fetchLocations = async () => {
    setLoading(true);
    try {
      // TODO: Replace with real API call to your backend
      // Example: GET /api/locations?state=CA&city=San&latMin=&latMax=&lngMin=&lngMax=&client=
      // const res = await fetch(`/api/locations?state=${stateCode}&city=${encodeURIComponent(city)}&latMin=${latMin ?? ''}&latMax=${latMax ?? ''}&lngMin=${lngMin ?? ''}&lngMax=${lngMax ?? ''}`);
      // const json = await res.json();
      // setLocations(json.data || json);
      // For now, filter mockData locally
      const filtered = mockData.filter(r => (
        (!stateCode || r.state === stateCode) &&
        (!city || r.city.toLowerCase().includes(city.toLowerCase())) &&
        (latMin === undefined || r.latitude >= latMin) &&
        (latMax === undefined || r.latitude <= latMax) &&
        (lngMin === undefined || r.longitude >= lngMin) &&
        (lngMax === undefined || r.longitude <= lngMax)
      ));
      setLocations(filtered);
    } catch (e) {
      message.error('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setClient('all');
    setStateCode('');
    setCity('');
    setLatMin(undefined);
    setLatMax(undefined);
    setLngMin(undefined);
    setLngMax(undefined);
    fetchLocations();
  };

  const openDetails = (record) => {
    setSelected(record);
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    setSelected(record || null);
    form.setFieldsValue(record || { city: '', state: '', latitude: undefined, longitude: undefined });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
      setLoading(true);
      // TODO: POST/PUT to /api/locations
      // If selected exists -> update, else create
      message.success(selected ? 'Location updated' : 'Location created');
      setEditOpen(false);
      fetchLocations();
    } catch (e) {
      if (e?.errorFields) return; // form validation error
      message.error('Failed to save location');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (record) => {
    Modal.confirm({
      title: `Delete ${record.city}, ${record.state}?`,
      content: 'This cannot be undone. Related market insights may prevent deletion.',
      okType: 'danger',
      onOk: async () => {
        setLoading(true);
        try {
          // TODO: DELETE /api/locations/:id
          message.success('Location deleted');
          fetchLocations();
        } catch (e) {
          message.error('Failed to delete location');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    { title: 'City', dataIndex: 'city', key: 'city', render: (t, r) => <Button type="link" onClick={() => openDetails(r)}>{t}</Button> },
    { title: 'State', dataIndex: 'state', key: 'state', width: 110 },
    { title: 'Latitude', dataIndex: 'latitude', key: 'latitude', width: 130 },
    { title: 'Longitude', dataIndex: 'longitude', key: 'longitude', width: 130 },
    { title: 'Zones', dataIndex: 'zones', key: 'zones', width: 110, render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Insights', dataIndex: 'insights', key: 'insights', width: 110, render: (v) => <Tag color="green">{v}</Tag> },
    { title: 'Actions', key: 'actions', width: 180, render: (_, r) => (
      <Space>
        <Button icon={<EnvironmentOutlined />} onClick={() => openDetails(r)} size="small">View</Button>
        <Button icon={<EditOutlined />} onClick={() => openEdit(r)} size="small">Edit</Button>
        <Button icon={<DeleteOutlined />} onClick={() => handleDelete(r)} size="small" danger>Delete</Button>
      </Space>
    )},
  ];

  const totalLocations = locations.length;
  const statesCovered = new Set(locations.map(l => l.state)).size;
  const avgZones = locations.length ? Math.round(locations.reduce((a, b) => a + (b.zones || 0), 0) / locations.length) : 0;

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 0 }}>WhiteLabel Location Management</Title>
          <Text type="secondary">Manage locations and zones. Filter by geography and export data. Ready for backend APIs.</Text>
        </div>

        {/* Stats */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic title="Total Locations" value={totalLocations} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic title="States Covered" value={statesCovered} />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Card>
              <Statistic title="Avg Zones/City" value={avgZones} />
            </Card>
          </Col>
        </Row>

        {/* Toolbar */}
        <Card bodyStyle={{ padding: 12 }}>
          <Row gutter={[12, 12]} align="middle">
            <Col xs={24} lg={4}>
              <Select value={client} onChange={setClient} style={{ width: '100%' }}>
                {clients.map(c => <Select.Option key={c} value={c}>{c === 'all' ? 'All Clients' : c}</Select.Option>)}
              </Select>
            </Col>
            <Col xs={24} lg={4}>
              <Select
                value={stateCode}
                onChange={setStateCode}
                placeholder="State"
                allowClear
                options={[
                  { value: 'CA', label: 'CA' },
                  { value: 'WA', label: 'WA' },
                  { value: 'TX', label: 'TX' },
                  { value: 'FL', label: 'FL' },
                ]}
              />
            </Col>
            <Col xs={24} lg={4}>
              <Input placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
            </Col>
            <Col xs={24} lg={10}>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber placeholder="Lat min" value={latMin} onChange={setLatMin} style={{ width: '25%' }} />
                <InputNumber placeholder="Lat max" value={latMax} onChange={setLatMax} style={{ width: '25%' }} />
                <InputNumber placeholder="Lng min" value={lngMin} onChange={setLngMin} style={{ width: '25%' }} />
                <InputNumber placeholder="Lng max" value={lngMax} onChange={setLngMax} style={{ width: '25%' }} />
              </Space.Compact>
            </Col>
            <Col xs={24} lg={2}>
              <Button icon={<CompassOutlined />} onClick={fetchLocations} type="default">Filter</Button>
            </Col>
            <Col xs={24} lg={6} style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={resetFilters}>Reset</Button>
                <Button icon={<UploadOutlined />}>Import CSV</Button>
                <Button icon={<DownloadOutlined />}>Export CSV</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>New Location</Button>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Table */}
        <Card title="Locations" bodyStyle={{ padding: 0 }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={locations}
            pagination={{ pageSize: 10 }}
          />
        </Card>

        {/* Details Drawer */}
        <Drawer
          title={selected ? `${selected.city}, ${selected.state}` : 'Location Details'}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={480}
        >
          {selected && (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Text type="secondary">Coordinates</Text>
              <Text>Lat: {selected.latitude}, Lng: {selected.longitude}</Text>
              <Divider />
              <Text type="secondary">Zones</Text>
              <Tag color="blue">{selected.zones}</Tag>
              <Divider />
              <Text type="secondary">Market Insights</Text>
              <Text>Insights count: <Tag color="green">{selected.insights}</Tag></Text>
              <Text type="secondary">Tip: Wire to GET /api/locations/{'{id}'}/market-insights for details</Text>
            </Space>
          )}
        </Drawer>

        {/* Create/Edit Modal */}
        <Modal
          title={selected ? 'Edit Location' : 'New Location'}
          open={editOpen}
          onCancel={() => setEditOpen(false)}
          onOk={handleSave}
          confirmLoading={loading}
          okText={selected ? 'Save Changes' : 'Create Location'}
        >
          <Form form={form} layout="vertical">
            <Form.Item label="City" name="city" rules={[{ required: true, message: 'City is required' }]}>
              <Input />
            </Form.Item>
            <Form.Item label="State" name="state" rules={[{ required: true, message: 'State is required' }]}>
              <Input maxLength={2} placeholder="e.g., CA" />
            </Form.Item>
            <Form.Item label="Latitude" name="latitude" rules={[{ required: true, message: 'Latitude is required' }]}>
              <InputNumber style={{ width: '100%' }} min={-90} max={90} step={0.0001} />
            </Form.Item>
            <Form.Item label="Longitude" name="longitude" rules={[{ required: true, message: 'Longitude is required' }]}>
              <InputNumber style={{ width: '100%' }} min={-180} max={180} step={0.0001} />
            </Form.Item>
          </Form>
        </Modal>
      </Space>
    </div>
  );
};

export default WhiteLabelLocations;
